import { Box, Cpu, FileText, GitBranch, Globe, KeyRound, Plug, Puzzle, Sparkles, X, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, api } from "../api";
import { Button, EmptyState, ErrorState, IconButton, LoadingState, ProviderMark } from "../components";
import { DEPENDENCY_KINDS, type DependencyGroup, type DependencyInput, type DependencyKind, dependencyKindLabels, normalizeDependency } from "../../shared/dependencies";
import { type DependenciesResponse, dependencyHeadline, familySections, providerMarkTitle } from "./loopDependencies";

export type LoopDependenciesState = {
  /** Grouped by identity (kind + name), as the API returns them. */
  groups: DependencyGroup[];
  loading: boolean;
  error: string;
  declare: (inputs: DependencyInput[]) => Promise<void>;
  remove: (identity: { kind: DependencyKind; name: string }) => Promise<void>;
  retry: () => void;
};

const KIND_ICONS: Record<DependencyKind, LucideIcon> = { mcp_server: Plug, plugin: Puzzle, skill: Sparkles, api: Globe, secret: KeyRound, file: FileText, repository: GitBranch, model: Cpu, other: Box };
const NAME_MAX = 120;
const REF_MAX = 300;

/**
 * Loads the dependencies of one loop and exposes declare / remove. A 404 reads as "nothing recorded"
 * rather than an error, so the panel stands before the worker route ships and after.
 */
export function useLoopDependencies(organizationId: string | undefined, loopId: string): LoopDependenciesState {
  const [groups, setGroups] = useState<DependencyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId || !loopId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api<DependenciesResponse>(`/api/app/loops/${loopId}/dependencies`, { signal: controller.signal }, organizationId)
      .then((data) => setGroups(data.groups ?? []))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        if (caught instanceof ApiError && caught.status === 404) {
          setGroups([]);
          return;
        }
        setError(caught instanceof Error ? caught.message : "Dependencies are unavailable.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, loopId, refreshKey]);

  const reload = useCallback(async () => {
    const data = await api<DependenciesResponse>(`/api/app/loops/${loopId}/dependencies`, {}, organizationId);
    setGroups(data.groups ?? []);
  }, [organizationId, loopId]);

  const declare = useCallback(async (inputs: DependencyInput[]) => {
    await api(`/api/app/loops/${loopId}/dependencies`, { method: "POST", body: JSON.stringify({ dependencies: inputs }) }, organizationId);
    await reload();
  }, [organizationId, loopId, reload]);

  const remove = useCallback(async (identity: { kind: DependencyKind; name: string }) => {
    await api(`/api/app/loops/${loopId}/dependencies`, { method: "DELETE", body: JSON.stringify(identity) }, organizationId);
    setGroups((current) => current.filter((group) => !(group.kind === identity.kind && group.name === identity.name)));
    await reload().catch(() => undefined);
  }, [organizationId, loopId, reload]);

  const retry = useCallback(() => setRefreshKey((value) => value + 1), []);

  return { groups, loading, error, declare, remove, retry };
}

/** One dependency: kind glyph, name, ref, optional chip, confidence, the marks of the environments that saw it, and the remove control. */
function DependencyRow({ group, readOnly, onRemove }: { group: DependencyGroup; readOnly: boolean; onRemove: (identity: { kind: DependencyKind; name: string }) => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const Icon = KIND_ICONS[group.kind] ?? Box;
  const kindLabel = dependencyKindLabels[group.kind] ?? dependencyKindLabels.other;

  async function remove() {
    setBusy(true);
    setError("");
    try {
      await onRemove({ kind: group.kind, name: group.name });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The dependency could not be removed.");
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <li className="loop-dependencies__row">
      <span className="loop-dependencies__glyph" title={kindLabel}><Icon size={14} aria-hidden="true" /><span className="sr-only">{kindLabel}</span></span>
      <div className="loop-dependencies__main">
        <span className="loop-dependencies__name"><strong title={group.name}>{group.name}</strong>{!group.required ? <small className="loop-dependencies__chip">optional</small> : null}</span>
        {group.ref ? <code title={group.ref}>{group.ref}</code> : null}
      </div>
      <span className="loop-dependencies__trail">
        <span className="loop-dependencies__confidence">{group.confidence}</span>
        {group.providers.length > 0 ? (
          <span className="loop-dependencies__marks">{group.providers.map((provider) => <span key={provider} title={providerMarkTitle(group, provider)}><ProviderMark provider={provider} size={16} /></span>)}</span>
        ) : null}
        {readOnly || confirming ? null : (
          <IconButton label={`Remove ${group.name}`} className="loop-dependencies__remove" onClick={() => setConfirming(true)}><X size={14} aria-hidden="true" /></IconButton>
        )}
      </span>
      {confirming ? (
        <span className="loop-dependencies__confirm" role="group" aria-label={`Remove ${group.name}?`}>
          <span>Remove?</span>
          <button type="button" onClick={() => void remove()} disabled={busy}>Yes</button>
          <button type="button" onClick={() => setConfirming(false)} disabled={busy} autoFocus>No</button>
        </span>
      ) : null}
      {error ? <small role="alert" className="loop-dependencies__error">{error}</small> : null}
    </li>
  );
}

/** "Declare a dependency": kind, name, ref, required. Refuses anything shaped like a secret value before it leaves the browser. */
function Composer({ onDeclare }: { onDeclare: (inputs: DependencyInput[]) => Promise<void> }) {
  const [kind, setKind] = useState<DependencyKind>("api");
  const [name, setName] = useState("");
  const [ref, setRef] = useState("");
  const [required, setRequired] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const canAdd = name.trim().length > 0 && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canAdd) return;
    const input = normalizeDependency({ kind, name, ref, required, confidence: "declared" });
    if (!input) {
      setFormError("That looks like a secret value. Record the secret's name and where it lives instead.");
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      await onDeclare([{ kind: input.kind, name: input.name, ref: input.ref, required: input.required }]);
      setName("");
      setRef("");
      setRequired(true);
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "The dependency could not be declared.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="loop-dependencies-composer" onSubmit={submit}>
      <div className="loop-dependencies-composer__head">Declare a dependency</div>
      <div className="loop-dependencies-composer__fields">
        <label>
          <span>Kind</span>
          <select value={kind} onChange={(event) => setKind(event.target.value as DependencyKind)} disabled={busy}>
            {DEPENDENCY_KINDS.map((value) => <option key={value} value={value}>{dependencyKindLabels[value]}</option>)}
          </select>
        </label>
        <label>
          <span>Name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="QuickBooks API" maxLength={NAME_MAX} disabled={busy} required />
        </label>
        <label className="loop-dependencies-composer__ref">
          <span>Ref</span>
          <input value={ref} onChange={(event) => setRef(event.target.value)} placeholder="Endpoint, version, or path. Never a secret value." maxLength={REF_MAX} disabled={busy} spellCheck={false} />
        </label>
      </div>
      {formError ? <div className="form-error" role="alert">{formError}</div> : null}
      <div className="loop-dependencies-composer__actions">
        <label className="loop-dependencies-composer__required"><input type="checkbox" checked={required} onChange={(event) => setRequired(event.target.checked)} disabled={busy} /> Required</label>
        <Button type="submit" variant="ghost" busy={busy} disabled={!canAdd}>Add</Button>
      </div>
    </form>
  );
}

/**
 * The Dependencies panel on loop detail: what the loop needs, grouped by family, with the environments
 * that observed each one. The loop's own workspace declares and removes; viewers of a shared loop read.
 * `embedded` drops the panel chrome and head so the page's own section row names it.
 */
export function DependenciesPanel({ state, readOnly, originName, embedded = false }: { state: LoopDependenciesState; readOnly: boolean; originName: string; embedded?: boolean }) {
  const { groups, loading, error, declare, remove, retry } = state;
  const sections = familySections(groups);
  const Wrapper = embedded ? "div" : "section";

  return (
    <Wrapper className={embedded ? "loop-dependencies-panel loop-dependencies-panel--embedded" : "panel cockpit-panel loop-dependencies-panel"} aria-label={embedded ? undefined : "Loop dependencies"}>
      {!embedded ? (
        <div className="panel__head">
          <div><p className="eyebrow">Observed per agent</p><h2>Dependencies</h2></div>
          <span className="loop-notes-count">{dependencyHeadline(groups)}</span>
        </div>
      ) : null}
      {loading ? (
        <LoadingState>Reading dependencies…</LoadingState>
      ) : error ? (
        <ErrorState action={<Button variant="secondary" onClick={retry}>Try again</Button>}>Dependencies unavailable: {error}</ErrorState>
      ) : sections.length > 0 ? (
        <ol className="loop-dependencies">
          {sections.map((section) => (
            <li key={section.family} className="loop-dependencies__family">
              <span className="loop-dependencies__family-label">{section.label}</span>
              <ul>{section.groups.map((group) => <DependencyRow key={`${group.kind}:${group.name}`} group={group} readOnly={readOnly} onRemove={remove} />)}</ul>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState eyebrow="Not recorded" title="No dependencies recorded">
          {readOnly
            ? `${originName} has not recorded what this loop needs. The next scan there reports what it can see.`
            : "The next scan reports what it can see; you can also declare them here or ask the agent to call declare_loop_dependencies."}
        </EmptyState>
      )}
      {!loading && !error ? (
        readOnly ? (
          <p className="loop-dependencies-readonly">Recorded in {originName}. Only that workspace can declare or remove one.</p>
        ) : (
          <Composer onDeclare={declare} />
        )
      ) : null}
    </Wrapper>
  );
}
